/**
 * Completion content for the "complete-intake" activity.
 * Pre-configures EncounterForm to only show Clinical Intake and CoCM Intake types.
 *
 * Care time (call + documentation minutes) is captured as a section on the
 * intake form's Details step and persisted as care-time Observations inside
 * the encounter's atomic transaction — there is no separate write here.
 * @agentic connections
 */

import { Alert, Skeleton, Text } from "@mantine/core";
import type { Encounter, Task } from "@medplum/fhirtypes";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useMedplum } from "@medplum/react";
import { EncounterFormShell } from "@/components/domain/encounter/EncounterFormShell";
import type { EncounterFormRef } from "@/components/domain/encounter/EncounterFormShell";
import { getResourceIdFromReference } from "@benchmark/core/utils";
import { findDraftEncounterForPatient } from "@benchmark/core/services";
import type { CompletionContentProps } from "./DefaultCompletionContent";
import { forwardRef, useState, useEffect } from "react";

const INTAKE_ENCOUNTER_TYPES = [
  "clinical-intake-interaction",
  "cocm-intake-interaction",
  "unbilled-intake-interaction",
];

/**
 * Extracts encounter context from a complete-intake task.
 * @agentic connections
 */
const deriveIntakeContext = (task: Task) => {
  const activityCode = task.code?.coding?.[0]?.code;
  const carePlanRef = task.basedOn?.find((entry) =>
    entry.reference?.startsWith("CarePlan/"),
  )?.reference;
  const patientRef = task.for?.reference;

  const patientId = patientRef
    ? getResourceIdFromReference(patientRef, "Patient")
    : undefined;
  const carePlanId = carePlanRef
    ? getResourceIdFromReference(carePlanRef, "CarePlan")
    : undefined;

  if (!activityCode || !patientId || !carePlanId || !task.id) {
    return undefined;
  }

  return {
    patientId,
    activityContext: {
      carePlanId,
      activityCode,
      taskId: task.id,
    },
  };
};

/**
 * Intake-specific completion content that detects existing draft encounters
 * and restricts encounter type selection to Clinical Intake and CoCM Intake only.
 * @agentic connections
 */
export const IntakeCompletionContent = forwardRef<
  EncounterFormRef,
  CompletionContentProps
>(
  (
    {
      task,
      onDone,
      onCancel,
      nextActivityLabel,
      nextActivityDefaultOwner,
      nextActivityAppliedRule,
      nextTaskOwnerOverride,
      onNextTaskOwnerOverrideChange,
      isResolvingNextTaskOwner = false,
    }: CompletionContentProps,
    ref,
  ) => {
    const medplum = useMedplum();
    const context = deriveIntakeContext(task);
    const patientId = context?.patientId;
    const [draftEncounter, setDraftEncounter] = useState<
      Encounter | undefined
    >();
    const [checkingDraft, setCheckingDraft] = useState(!!patientId);

    useEffect(() => {
      if (!patientId) return;

      let cancelled = false;
      findDraftEncounterForPatient(
        medplum,
        patientId,
        INTAKE_ENCOUNTER_TYPES,
      ).then((enc) => {
        if (!cancelled) {
          setDraftEncounter(enc);
          setCheckingDraft(false);
        }
      });

      return () => {
        cancelled = true;
      };
    }, [medplum, patientId]);

    if (!context) {
      return (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          title="Unable to open intake completion"
        >
          <Text size="sm">
            Missing patient or care plan context for this activity. Please
            refresh and try again.
          </Text>
        </Alert>
      );
    }

    if (checkingDraft) {
      return <Skeleton height={200} />;
    }

    return (
      <EncounterFormShell
        ref={ref}
        patientId={context.patientId}
        encounter={draftEncounter}
        activityContext={context.activityContext}
        restrictToEncounterTypes={INTAKE_ENCOUNTER_TYPES}
        nextActivityLabel={nextActivityLabel}
        nextActivityDefaultOwner={nextActivityDefaultOwner}
        nextActivityAppliedRule={nextActivityAppliedRule}
        nextTaskOwnerOverride={nextTaskOwnerOverride}
        onNextTaskOwnerOverrideChange={onNextTaskOwnerOverrideChange}
        isResolvingNextTaskOwner={isResolvingNextTaskOwner}
        onSuccess={() => onDone()}
        onCancel={onCancel}
      />
    );
  },
);
